<script setup lang="ts">
import type { Schemas } from "#shopware";
import { getCategoryRoute, getTranslatedProperty } from "@shopware/helpers";
type NavigationElement = Schemas["Category"] & {
  activeClass?: boolean;
};

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { category } = useCategory();
const { advancedSearch: categorySearch } = useCategorySearch();
const categoryLoading = ref(true);
const { data: categoryResponse, error } = await useAsyncData(
  "cmsNavigationSubCategories" + category.value?.id,
  async () => {
    const data = await categorySearch({
      withCmsAssociations: false,
      query: {
        filter: [
          {
            type: "equals",
            field: "id",
            value: category.value?.id,
          },
        ],
        associations: {
          children: {
            associations: {
              seoUrls: {},
              children: {
                associations: {
                  seoUrls: {},
                  children: {
                    associations: {
                      seoUrls: {},
                    },
                  },
                },
              },
            },
          },
          seoUrls: {},
        },
      },
    });
    return data?.at(0) || ({} as NavigationElement);
  }
);

const expandedIds = ref<string>("");

function isCollapsed(navigationelement: Schemas["Category"]): boolean {
  return expandedIds.value == navigationelement.id;
}

const toggleCollapse = (navigationElement: Schemas["Category"]) => {
  if (isCollapsed(navigationElement)) {
    expandedIds.value = "";
  } else {
    expandedIds.value = navigationElement.id;
  }
};

const subCategories = computed(
  () => categoryResponse?.value?.children || ([] as NavigationElement[])
);

const isCategoryFilterVisible = ref<boolean>(true);
const toggleCategoryFilter = () => {
  isCategoryFilterVisible.value = !isCategoryFilterVisible.value;
};
onMounted(async () => {
  categoryLoading.value = false;
});

</script>
<template>
  <div
    class="accordion-item item overflow-hidden border-b border-b-cust_green mb-[80px]"
    v-if="subCategories.length && !categoryLoading"
    id="accordion-categories"
  >
    <div>
      <button
        class="accordion-header w-full text-left py-4 px-3 font-bold flex justify-between items-center cursor-pointer bg-transparent border-b border-b-cust_green"
        @click="toggleCategoryFilter"
      >
        <h6
          class="text-22 font-extrabold font-redit_sans_con tracking-[0.22px] capitalize"
        >
          {{ $t("plubel.listing.categories") }}
        </h6>
        <svg
          width="17"
          height="14"
          viewBox="0 0 16 9"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          class="transform transition-transform duration-200"
          :class="[!isFilterVisible ? 'rotate-0' : 'rotate-180']"
        >
          <path
            d="M15 1L7.99998 7L0.999983 1"
            stroke="#5C6566"
            stroke-width="2"
            stroke-linecap="round"
          />
        </svg>
      </button>
      <transition name="fade" mode="out-in">
        <div
          class="accordion-body item px-3"
          :class="[!isCategoryFilterVisible ? 'hidden' : '']"
        >
          <ul>
            <li v-for="category in subCategories">
              <NuxtLink :to="formatLink(getCategoryRoute(category))">
                {{ getTranslatedProperty(category, "name") }}
              </NuxtLink>
              <ul v-if="category.childCount > 0">
                <li v-for="subCategory in category.children" class="block ml-4">
                  <div class="flex justify-between">
                    <NuxtLink :to="formatLink(getCategoryRoute(subCategory))">
                      {{ getTranslatedProperty(subCategory, "name") }}
                    </NuxtLink>
                    <span
                      v-if="subCategory.children.length > 0"
                      class="right-[10px] relative cursor-pointer"
                      @click="toggleCollapse(subCategory)"
                    >
                      <i
                        class="fa-solid fa-angle-right submenu-arrow"
                        :class="{
                          'rotate-90':
                            expandedIds == subCategory.id ||
                            (subCategory as NavigationElement).activeClass,
                        }"
                      >
                      </i>
                    </span>
                  </div>
                  <ul
                    v-if="subCategory.childCount > 0"
                    :class="[
                      expandedIds == subCategory.id ||
                      (subCategory as NavigationElement).activeClass
                        ? 'flex'
                        : '!hidden',
                    ]"
                  >
                    <li
                      v-for="nestedCategory in subCategory.children"
                      class="block ml-4"
                    >
                      <NuxtLink
                        :to="formatLink(getCategoryRoute(nestedCategory))"
                      >
                        {{ getTranslatedProperty(nestedCategory, "name") }}
                      </NuxtLink>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </transition>
    </div>
    <div v-if="categoryLoading" class="loading-skeleton space-y-4">
      <div v-for="i in 4" :key="i" class="space-y-3">
        <!-- Accordion header -->
        <div
          class="h-10 bg-cust_gray02 rounded animate-pulse flex items-center px-4"
        >
          <div class="w-24 h-4 bg-gray-300 rounded"></div>
        </div>
        <!-- Accordion content -->
        <div class="px-0 space-y-2">
          <div
            v-for="j in 3"
            :key="j"
            class="h-4 bg-gray-300 rounded animate-pulse"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>
